import { useState, useCallback } from 'react';
import { toast } from 'sonner';

interface UseImageUploadOptions {
  maxWidth?: number;
  maxHeight?: number;
  maxSizeInBytes?: number; // Optional check for initial file size
  quality?: number; // 0 to 1 for jpeg/webp
}

interface UseImageUploadResult {
  isProcessing: boolean;
  processFile: (file: File) => Promise<string | null>;
}

export function useImageUpload({
  maxWidth = 200,
  maxHeight = 200,
  maxSizeInBytes = 5 * 1024 * 1024, // 5MB default limit for initial file
  quality = 0.8,
}: UseImageUploadOptions = {}): UseImageUploadResult {
  const [isProcessing, setIsProcessing] = useState(false);

  const processFile = useCallback(
    async (file: File): Promise<string | null> => {
      // Basic validation
      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return null;
      }

      if (file.size > maxSizeInBytes) {
        toast.error(`Image size should be less than ${maxSizeInBytes / 1024 / 1024}MB`);
        return null;
      }

      setIsProcessing(true);

      return new Promise<string | null>((resolve) => {
        const reader = new FileReader();

        reader.onload = (readerEvent) => {
          const img = new Image();

          img.onload = () => {
            // Resize logic
            let width = img.width;
            let height = img.height;

            // Calculate new dimensions while maintaining aspect ratio
            if (width > height) {
              if (width > maxWidth) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
              }
            } else {
              if (height > maxHeight) {
                width = Math.round((width * maxHeight) / height);
                height = maxHeight;
              }
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
              toast.error('Could not process image');
              setIsProcessing(false);
              resolve(null);
              return;
            }

            // Draw image on canvas
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to base64
            // Using image/jpeg usually results in smaller size than png for photos
            // But if transparency is needed, png is better. For avatars, jpeg is usually fine.
            const dataUrl = canvas.toDataURL('image/jpeg', quality);

            setIsProcessing(false);
            resolve(dataUrl);
          };

          img.onerror = () => {
            toast.error('Failed to load image');
            setIsProcessing(false);
            resolve(null);
          };

          if (readerEvent.target?.result) {
            img.src = readerEvent.target.result as string;
          } else {
            toast.error('Failed to read file');
            setIsProcessing(false);
            resolve(null);
          }
        };

        reader.onerror = () => {
          toast.error('Error reading file');
          setIsProcessing(false);
          resolve(null);
        };

        reader.readAsDataURL(file);
      });
    },
    [maxHeight, maxWidth, maxSizeInBytes, quality],
  );

  return { isProcessing, processFile };
}
